import React, { useEffect, useState } from 'react';
import { data } from 'configs/menu-config';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import {
  Button,
  Descriptions,
  Form,
  Input,
  notification,
} from 'antd';
import authService from 'services/auth';
import { useDispatch, useSelector } from 'react-redux';
import { setUserData } from 'redux/slices/auth';
import { fetchRestSettings } from 'redux/slices/globalSettings';
import { useTranslation } from 'react-i18next';
import { PROJECT_NAME, RECAPTCHASITEKEY } from 'configs/app-global';
import Recaptcha from 'components/recaptcha';
import { clearUserChats } from 'redux/slices/chat';


const loginFormStyles = `
  .login-form-antd .ant-form-item-label > label { color: #1e293b; font-weight: 600; font-size: 0.875rem; }
  .login-form-antd .ant-input-affix-wrapper .ant-input-prefix,
  .login-form-antd .ant-input-password .ant-input-prefix { margin-right: 10px; }
  .login-form-antd .ant-input-affix-wrapper,
  .login-form-antd .ant-input-password { height: 48px !important; border-radius: 12px; border-color: #e2e8f0; padding: 0 14px; }
  .login-form-antd .ant-input-affix-wrapper:focus,
  .login-form-antd .ant-input-affix-wrapper-focused,
  .login-form-antd .ant-input-password:focus { border-color: #0f766e !important; box-shadow: 0 0 0 3px rgba(15,118,110,0.15) !important; }
  .login-form-antd .ant-btn-primary { background: #0f766e !important; border-color: #0f766e !important; }
  .login-form-antd .ant-btn-primary:hover { background: #0d9488 !important; border-color: #0d9488 !important; }
`;

const credentials = [
  { login: 'owner@githubit.com', password: 'githubit' },
  { login: 'manager@githubit.com', password: 'manager' },
];

const Login = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const isMountedRef = React.useRef(true);
  const { settings } = useSelector((state) => state.globalSettings);
  const [recaptcha, setRecaptcha] = useState(null);
  const handleRecaptchaChange = (value) => setRecaptcha(value);
  const isDemo = Boolean(Number(settings?.is_demo));

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleLogin = (values) => {
    const body = { password: values.password };
    if (values.email.includes('@')) {
      body.email = values.email;
    } else {
      body.phone = values.email.replace(/[^0-9]/g, '');
    }
    setLoading(true);
    authService
      .login(body)
      .then((res) => {
        const user = {
          fullName: res.data.user.firstname + ' ' + res.data.user.lastname,
          role: res.data.user.role,
          urls: data[res.data.user.role] || data.admin,
          img: res.data.user.img,
          token: res.data.access_token,
          email: res.data.user.email,
          id: res.data.user.id,
          shop_id: res.data.user?.shop?.id,
        };
        if (user.role === 'user') {
          notification.error({ message: t('ERROR_101') });
          return;
        }
        if (!(data[user.role] || data.admin)) {
          notification.error({
            message: t('Unsupported role') || 'This role is not supported.',
          });
          return;
        }
        localStorage.setItem('token', res.data.access_token);
        dispatch(setUserData(user));
        dispatch(fetchRestSettings());
        dispatch(clearUserChats());
      })
      .catch((err) => {
        const status = err?.response?.status;
        const msg = err?.response?.data?.message || err?.message;
        if (status === 500) {
          notification.error({
            message: t('Server error') || 'Server error',
            description: t('Please try again later or contact support.') || 'Please try again later or contact support.',
          });
        } else if (msg) {
          notification.error({ message: msg });
        } else {
          notification.error({
            message: t('Login failed') || 'Login failed',
            description: t('Please check your connection and try again.') || 'Please check your connection and try again.',
          });
        }
      })
      .finally(() => {
        if (isMountedRef.current) setLoading(false);
      });
  };

  const copyCredentials = (event, item) => {
    event.preventDefault();
    form.setFieldsValue({ email: item.login, password: item.password });
  };

  useEffect(() => {
    dispatch(fetchRestSettings());
  }, [dispatch]);

  return (
    <div className='fixed inset-0 min-h-screen w-full flex items-center justify-center p-4 sm:p-6 overflow-hidden bg-white'>
      {/*
        A plain white page with the shop's own mark on it.

        This used to pull a stock photograph of a desk from Unsplash and dim it
        behind a translucent card. It was somebody else's photograph, fetched
        from somebody else's server on every visit to the staff sign-in page,
        and it said nothing about this shop. The logo does.
      */}
      <div
        className='pointer-events-none absolute inset-0 z-0 flex items-center justify-center overflow-hidden'
        aria-hidden
      >
        <img
          src='/logo.png'
          alt=''
          draggable={false}
          style={{
            width: 'min(120vw, 820px)',
            maxWidth: 'none',
            opacity: 0.05,
            filter: 'grayscale(1)',
            userSelect: 'none',
          }}
        />
      </div>
      {/* The sign-in card */}
      <div className='relative z-[2] w-full max-w-[420px] mx-auto flex-shrink-0'>
        <div className='relative rounded-2xl overflow-hidden bg-white shadow-[0_25px_50px_-12px_rgba(15,23,42,0.12)] ring-1 ring-slate-200'>
          {/* Top accent */}
          <div className='h-1 w-full bg-gradient-to-r from-teal-700 via-teal-500 to-teal-700' />
          <div className='px-8 pt-10 pb-9'>
            <div className='text-center mb-8'>
              <img
                src='/logo.png'
                alt={settings?.title || PROJECT_NAME}
                style={{
                  display: 'block',
                  height: 56,
                  width: 'auto',
                  margin: '0 auto 16px',
                  objectFit: 'contain',
                }}
              />
              <h1 className='text-2xl font-bold text-slate-800 tracking-tight'>
                {settings?.title || PROJECT_NAME}
              </h1>
              <p className='mt-2 text-slate-500 text-sm font-medium'>
                {t('Sign in to your account')}
              </p>
            </div>
            <Form
              name='login-form'
              layout='vertical'
              form={form}
              onFinish={handleLogin}
              size='large'
              className='login-form-antd'
            >
              <Form.Item
                name='email'
                label={t('Email')}
                rules={[{ required: true, message: t('Please input your Email!') }]}
                className='mb-4'
              >
                <Input
                  prefix={<MailOutlined className='text-slate-400' />}
                  placeholder={t('Email or phone')}
                />
              </Form.Item>
              <Form.Item
                name='password'
                label={t('Password')}
                rules={[{ required: true, message: t('Please input your password!') }]}
                className='mb-4'
              >
                <Input.Password
                  prefix={<LockOutlined className='text-slate-400' />}
                  placeholder={t('Password')}
                />
              </Form.Item>
              <Recaptcha onChange={handleRecaptchaChange} />
              <Form.Item className='mt-6 mb-0'>
                <Button
                  type='primary'
                  htmlType='submit'
                  loading={loading}
                  disabled={Boolean(RECAPTCHASITEKEY?.trim()) && !Boolean(recaptcha)}
                  className='w-full h-12 rounded-xl font-semibold text-base border-0'
                  style={{ backgroundColor: '#0f766e', borderColor: '#0f766e' }}
                >
                  {t('Login')}
                </Button>
              </Form.Item>
            </Form>

            {isDemo && (
              <div className='mt-8 pt-7 border-t border-slate-200'>
                <div className='text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3'>
                  {t('Demo credentials')}
                </div>
                <Descriptions bordered size='small'>
                  {credentials.map((item, idx) => (
                    <React.Fragment key={idx}>
                      <Descriptions.Item span={2} label={item.login}>
                        {item.password}
                      </Descriptions.Item>
                      <Descriptions.Item span={1}>
                        <a
                          href='/'
                          className='text-primary font-semibold text-[0.8125rem] hover:text-primary-hover'
                          onClick={(e) => copyCredentials(e, item)}
                        >
                          {t('Copy')}
                        </a>
                      </Descriptions.Item>
                    </React.Fragment>
                  ))}
                </Descriptions>
              </div>
            )}
            {!isDemo && process.env.REACT_APP_IS_DEMO === 'true' && (
              <div className='mt-8 pt-7 border-t border-slate-200'>
                <Descriptions bordered size='small'>
                  <Descriptions.Item span={4} label={t('Login')}>
                    {credentials[0].login}
                  </Descriptions.Item>
                  <Descriptions.Item span={4} label={t('Password')}>
                    {credentials[0].password}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            )}
          </div>
        </div>
      </div>
      <style>{loginFormStyles}</style>
    </div>
  );
};

export default Login;
